import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCognitiveResult } from '../../../services/assessmentService';

const axes = ['WM', 'PS', 'FR', 'AF', 'SI'];

function buildRadarPoints(scores) {
  const center = 120;
  const radius = 90;
  return scores.map((score, idx) => {
    const angle = (Math.PI * 2 * idx) / scores.length - Math.PI / 2;
    const r = (Math.max(0, Math.min(100, score)) / 100) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  });
}

function polygonString(points) {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

export default function ResultPage() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);

  useEffect(() => {
    const preload = async () => {
      const cached = sessionStorage.getItem('cognitive_result_payload');
      if (cached) {
        setResult(JSON.parse(cached));
        return;
      }

      const sessionId = sessionStorage.getItem('cognitive_session_id');
      if (sessionId) {
        const payload = await getCognitiveResult(sessionId);
        setResult(payload);
      }
    };

    preload();
  }, []);

  const sectionRows = useMemo(() => result?.sectionScores || [], [result]);
  const sectionScoreMap = useMemo(() => {
    const map = {
      COG_WM: 0,
      COG_PS: 0,
      COG_FR: 0,
      COG_AF: 0,
      COG_SI: 0
    };
    sectionRows.forEach((row) => {
      map[row.sectionCode] = row.score;
    });
    return map;
  }, [sectionRows]);

  const radarScores = [
    sectionScoreMap.COG_WM,
    sectionScoreMap.COG_PS,
    sectionScoreMap.COG_FR,
    sectionScoreMap.COG_AF,
    sectionScoreMap.COG_SI
  ];

  const points = buildRadarPoints(radarScores);
  const iqEstimate = result?.iqEstimate || 'High Average: 110-119';

  const strengths = [...sectionRows]
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .map((row) => row.sectionName);

  const improvements = [...sectionRows]
    .sort((a, b) => a.score - b.score)
    .slice(0, 2)
    .map((row) => row.sectionName);

  if (!result) {
    return <div className="min-h-screen cognitive-intro-bg flex items-center justify-center text-white">Loading results...</div>;
  }

  return (
    <div className="min-h-screen cognitive-intro-bg px-4 py-10 text-white">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl font-outfit text-center">NeuroPeak Cognitive Battery</h1>
        <p className="text-center text-white/70 mt-2">Cognitive Performance Profile</p>

        <div className="mt-8 cognitive-panel rounded-3xl p-6 md:p-8">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <p className="text-white/70">Overall Cognitive Score</p>
              <p className="text-6xl font-bold mt-2">{Math.round(Number(result.totalScore || 0))}%</p>
              <span className="inline-block mt-3 rounded-full border border-violet-300/30 bg-violet-500/20 px-3 py-1 text-sm text-violet-200">
                {iqEstimate}
              </span>
              <p className="text-xs text-white/65 mt-2">Estimate is indicative and not a clinical IQ diagnosis.</p>
            </div>

            <div className="flex justify-center">
              <svg viewBox="0 0 240 240" className="w-[260px] h-[260px]">
                {[1, 2, 3, 4].map((layer) => {
                  const layerPoints = buildRadarPoints(Array(5).fill((layer / 4) * 100));
                  return (
                    <polygon
                      key={`grid_${layer}`}
                      points={polygonString(layerPoints)}
                      fill="none"
                      stroke="rgba(255,255,255,0.18)"
                      strokeWidth="1"
                    />
                  );
                })}
                <polygon points={polygonString(points)} fill="rgba(124,58,237,0.35)" stroke="#8b5cf6" strokeWidth="2" />
                {axes.map((axis, idx) => {
                  const angle = (Math.PI * 2 * idx) / axes.length - Math.PI / 2;
                  const x = 120 + 102 * Math.cos(angle);
                  const y = 120 + 102 * Math.sin(angle);
                  return (
                    <text key={axis} x={x} y={y} textAnchor="middle" className="fill-white text-[11px]">
                      {axis}
                    </text>
                  );
                })}
              </svg>
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-white/20 bg-white/10 p-4">
              <p className="text-sm text-white/70">Strengths</p>
              <ul className="mt-2 space-y-1 text-sm">
                {strengths.map((entry) => <li key={entry}>• {entry}</li>)}
              </ul>
            </div>
            <div className="rounded-xl border border-white/20 bg-white/10 p-4">
              <p className="text-sm text-white/70">Areas to Improve</p>
              <ul className="mt-2 space-y-1 text-sm">
                {improvements.map((entry) => <li key={entry}>• {entry}</li>)}
              </ul>
            </div>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/20 text-white/70">
                  <th className="py-2">Section</th>
                  <th className="py-2">Score</th>
                  <th className="py-2">Accuracy</th>
                  <th className="py-2">Percentile</th>
                </tr>
              </thead>
              <tbody>
                {sectionRows.map((row) => (
                  <tr key={row.sectionCode} className="border-b border-white/10">
                    <td className="py-2">{row.sectionName}</td>
                    <td className="py-2">{row.score}%</td>
                    <td className="py-2">{row.accuracy}%</td>
                    <td className="py-2">{row.percentile || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-xl bg-gradient-to-r from-violet-600 to-purple-700 px-4 py-2.5 font-semibold"
            >
              Download Full Cognitive Report
            </button>
            <button
              type="button"
              onClick={() => navigate('/assessment')}
              className="rounded-xl border border-white/25 bg-white/10 px-4 py-2.5"
            >
              Back to Hub
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
